import * as React from "react";
import { useNavigate, generatePath } from 'react-router';
import { AppContext } from "app/context/AppContext";
import { useContext } from "react";
import { DataTable } from "../DataTable";
import { renderToStaticMarkup } from "react-dom/server";
import { ModuleTypes } from "../ModuleType";

/**
 * ClientOverviewComponent
 * @constructor
 */
const ClientOverviewComponent = () => {
  const navigate = useNavigate();
  const {
    services: {
      serviceClient
    },
    I18N,
    getRoutes
  } = useContext(AppContext);

  /**
   * ROUTE_CONFIG
   */
  const ROUTE_CONFIG = getRoutes();

  /**
   * handleView
   * @param id
   */
  const handleView = (id) => {
    navigate(generatePath(ROUTE_CONFIG.CLIENT_VIEW_EDIT.path, {
      id: id
    }));
  };

  /**
   * handleTest
   * @param id
   */
  const handleTest = (id) => {
    navigate(generatePath(ROUTE_CONFIG.CLIENT_TEST.path, {
      id: id
    }));
  };

  /**
   * handleModuleTypes
   * @param config
   */
  const handleModuleTypes = (config: any) => {
    // @ts-ignore
    return renderToStaticMarkup(<ModuleTypes config={ config } I18N={ window.ClientOverviewComponent.I18N } />);
  };

  /**
   * static variables
   */
  // @ts-ignore
  window.ClientOverviewComponent = {
    serviceClient: serviceClient,
    handleModuleTypes: handleModuleTypes,
    handleView: handleView,
    handleTest: handleTest,
    I18N: I18N,
    ROUTE_CONFIG_CLIENT_TEST: ROUTE_CONFIG.CLIENT_TEST
  };

  /**
   * header
   */
  const header = [
    { text: "Partner ID", value: "partner_id" },
    { text: "Token", value: "token" },
    { text: "ID", value: "id" },
    { text: "Comment", value: "comment" },
    { text: "Enabled", value: "enabled" },
    { text: "Config", value: "config" },
    { text: "Created At", value: "created_at" },
    { text: "Updated At", value: "updated_at" },
    { text: "Type", value: "type" }
  ];

  /**
   * columns
   */
  // every VISIBLE column needs renderHeader + renderHeaderReplace, and array
  // index 0 must hold a hidden column: the bundle's tableRender turns each
  // heading text node into a broken {nodeName:"P", data} hybrid that diff-dom
  // can neither match nor replace (the render loop then never converges and
  // freezes the main thread); renderHeaderReplace swaps in a clean node, but
  // tableRender skips the replacement for array index 0 (falsy findIndex), so
  // no visible column may sit there. select keeps the data mapping intact.
  const columns = [
    {
      select: 1,
      cellClass: "data-token",
      headerClass: "data-token",
      hidden: true
    },
    {
      select: 0,
      cellClass: "data-client-id",
      headerClass: "data-client-id",
      render: function (_data, _element, _rowIndex, _cellIndex, _row) {
        let data = _row[2].data || [],
          // @ts-ignore
          name = data?.[0]?.data || window.ClientOverviewComponent.I18N.t("Unbekannt");

        return name;
      },
      renderHeader: function () {
        // @ts-ignore
        return window.ClientOverviewComponent.I18N.t("Partner ID")
      },
      renderHeaderReplace: true
    },
    {
      select: 2,
      cellClass: "data-id",
      headerClass: "data-id",
      hidden: true
    },
    {
      select: 3,
      cellClass: "data-comment",
      headerClass: "data-comment",
      hidden: true
    },
    {
      select: 4,
      cellClass: "data-enabled",
      headerClass: "data-enabled",
      render: function (_data, _element, _rowIndex, _cellIndex, _row) {
        // read-only state display: disabled actually blocks the visual toggle
        // (readonly does not on checkboxes), the aria-label carries the state
        let stateLabel = _data
          // @ts-ignore
          ? window.ClientOverviewComponent.I18N.t("Status: aktiv")
          // @ts-ignore
          : window.ClientOverviewComponent.I18N.t("Status: inaktiv");

        return `<dia-switch data-state-display>
          <input type="checkbox" id="status-switch-${_rowIndex}" ${_data ? 'checked' : ''} disabled aria-label="${stateLabel}" />
          <label for="status-switch-${_rowIndex}" aria-hidden="true"></label>
        </dia-switch>`;
      },
      renderHeader: function (){
        // @ts-ignore
        return window.ClientOverviewComponent.I18N.t("Status");
      },
      renderHeaderReplace: true
    },
    {
      select: 5,
      cellClass: "data-config",
      headerClass: "data-config",
      type: "object",
      render: function (_data, _element, _rowIndex, _cellIndex, _row) {
        let data = _row[8].data || [],
          type = data?.[0]?.data || "unknown",
          // the module-level flag wins when it is set, otherwise any enabled
          // sub-module (price/stock) counts, because the API sends
          // enabled: null on the client config. older payloads carried the
          // flag as a string ("true"/"false"), where "false" is truthy --
          // both spellings are normalized before they decide anything
          isEnabled = (_value) => _value === true || _value === "true",
          config = _data || {},
          subModules = [config.price, config.stock].filter(Boolean),
          enabled = config.enabled == null
            ? subModules.some((_subModule) => isEnabled(_subModule.enabled))
            : isEnabled(config.enabled);

        // @ts-ignore
        return window.ClientOverviewComponent.handleModuleTypes({
          [type]: {
            state: enabled ? "active" : false
          }
        });
      },
      renderHeader: function () {
        // @ts-ignore
        return window.ClientOverviewComponent.I18N.t("Module")
      },
      renderHeaderReplace: true
    },
    {
      select: 6,
      cellClass: "data-created-at",
      headerClass: "data-created-at",
      hidden: true
    },
    {
      select: 7,
      cellClass: "data-updated-at",
      headerClass: "data-updated-at",
      hidden: true
    },
    {
      select: 8,
      cellClass: "data-type",
      headerClass: "data-type",
      render: function (_data, _element, _rowIndex, _cellIndex, _row, _headings) {
        let data = _row[2].data || [],
          id = data?.[0]?.data;

        return `<button
            data-small
            data-primary-dark
            data-icon-only
            title="${
              // @ts-ignore
              window.ClientOverviewComponent.I18N.t('Client Daten anzeigen')
            }"
            onClick="window.ClientOverviewComponent.handleView('${id}')"
          >
            <i data-icon-before="eye"></i>
          </button>
          <button
            data-small
            data-secondary
            data-icon-only
            onClick="window.ClientOverviewComponent.handleTest('${id}')"
            title="${
              /* @ts-ignore */
              window.ClientOverviewComponent.ROUTE_CONFIG_CLIENT_TEST.title
            }"
          >
            <i data-icon-before="${
            /* @ts-ignore */
            window.ClientOverviewComponent.ROUTE_CONFIG_CLIENT_TEST.icon
          }"></i>
          </button>`
      },
      renderHeader: function () {
        // @ts-ignore
        return window.ClientOverviewComponent.I18N.t("Aktion")
      },
      renderHeaderReplace: true
    }
  ];

  /**
   * options
   */
  const options = {
    searchable: true,
    // server-side table: the component sorts only the loaded page and sends
    // no sort parameter, so sorters would promise more than they deliver
    sortable: false,
    paging: true,
    perPage: 10,
    // TEMP DIAG: bounded diff loop so a non-converging diff throws instead of freezing
    diffDomOptions: { debug: true, diffcap: 200 }
  };

  /**
   * render
   */
  return (
    <DataTable
      key="clientDataTable"
      tableId="clientDataTable"
      header={ header }
      options={ options }
      columns={ columns }
      server-side="true"
      checkboxes={ false }
      customService={ serviceClient }
      customServiceName={ "getDatatable" }
    />
  );
};

export default ClientOverviewComponent;
